import { datadogRum } from '@datadog/browser-rum'
import { scrollInstantWithCorrection } from '~/utils/scroll'
import { REHYDRATION_DEBOUNCE_MS } from '~/constants/markings'
import { placeMarkingInContainer } from '~/utils/markings/rehydrate'
import { allMarkElements, isMarkingColor, markElementsFor, recolorMarkingDom, setMarkingNoteIndicator, unwrapMarking } from '~/utils/markings'

/**
 * Re-applies persisted markings to the DOM. Content containers are populated
 * asynchronously (Solr fetches finish after mount), so a single nextTick run
 * isn't enough — a MutationObserver per container schedules another pass
 * whenever the content changes. They stay attached for as long as the view is
 * mounted, because content keeps changing: commentary switches, footnote
 * toggles and collapsed panels all replace text that carries markings.
 */
export function useMarkingsRehydration(
  containers: Record<ContainerKey, Readonly<Ref<HTMLDivElement | null>>>,
  containerKeys: readonly ContainerKey[] = CONTAINER_KEYS,
) {
  const { allMarkings, markingsForPage, getCurrentPageUrl, isReady, placement } = useMarkings()
  const route = useRoute()
  const reportedFailures = new Set<string>()

  const observers = new Map<ContainerKey, MutationObserver>()

  // Links from MarkingDetail carry `?marking=<id>` — scroll to that mark once
  // wrapped. Track the last id so retry passes don't fight the user's scroll.
  let lastScrolledMarkingId: string | null = null

  function scrollToTargetMarking() {
    const queryValue = route.query.marking
    const id = typeof queryValue === 'string' ? queryValue : null

    if (!id || id === lastScrolledMarkingId) {
      return
    }

    // A marking can be wrapped inside a column that is not displayed: the
    // commentary column is `hidden lg:flex`, and reading views show one column
    // at a time below desktop. Such elements measure as zero, so scrolling
    // them silently does nothing — skip them and let a later pass retry once
    // the column is on screen. `lastScrolledMarkingId` stays unset meanwhile.
    const markElement = Array.from(markElementsFor(id))
      .find(element => element.getClientRects().length > 0)
    // Reading views scroll inside their column, not the window, so the mark's
    // own scroll parent is the one to move.
    const container = markElement?.closest<HTMLElement>('[data-container]')

    if (!markElement || !container) {
      return
    }

    lastScrolledMarkingId = id
    // Same helper the table of contents uses: it measures the sticky header
    // instead of guessing around it, and re-checks on the next frame so
    // content still streaming in cannot leave the mark off screen.
    scrollInstantWithCorrection(markElement, container)
  }

  watch(() => route.fullPath, () => {
    lastScrolledMarkingId = null
  })

  function rehydrate(changedIds?: Set<string>): void {
    // A targeted pass only looks at `changedIds`, so records queued before it
    // ran describe content it will leave alone — and the discard at the end
    // would drop them. Hand those to a full pass instead.
    if (changedIds && discardOwnMutations()) {
      debouncedRehydrate()
    }

    const markingsOnPage = markingsForPage(getCurrentPageUrl())
    const markingsById = new Map(markingsOnPage.map(marking => [marking.id, marking]))
    const elementsById = new Map<string, HTMLElement[]>()

    for (const element of allMarkElements()) {
      const id = element.dataset.markingId

      if (id) {
        const elements = elementsById.get(id) ?? []
        elements.push(element)
        elementsById.set(id, elements)
      }
    }

    for (const [id, elements] of elementsById) {
      if (changedIds && !changedIds.has(id)) {
        continue
      }

      const marking = markingsById.get(id)

      if (!marking) {
        unwrapMarking(id)
        setMarkingNoteIndicator(id, false, elements)
      }
      else if (isMarkingColor(marking.color)) {
        recolorMarkingDom(id, marking.color, elements)
        setMarkingNoteIndicator(id, !!marking.note?.trim(), elements)
      }
    }

    // Most passes find everything applied and skip the placement below, which
    // is what collects the unit text. A marking spanning several units is
    // re-placed every pass, because one wrapped `<mark>` says nothing about
    // whether its other units arrived. Single-unit markings settle on the
    // cheap check.
    const missing = markingsOnPage.filter(marking => isMarkingColor(marking.color)
      && (!changedIds || changedIds.has(marking.id))
      && ((marking.segments?.length ?? 1) > 1 || !elementsById.has(marking.id)))

    if (missing.length) {
      for (const containerKey of containerKeys) {
        const root = toValue(containers[containerKey])

        if (!root) {
          continue
        }

        for (const marking of missing) {
          const result = placeMarkingInContainer(root, marking)

          if (result.failed || result.renderFailed) {
            placement.value[marking.id] = 'unplaced'

            if (!reportedFailures.has(marking.id)) {
              reportedFailures.add(marking.id)
              datadogRum.addError(new Error('Marking could not be placed'), {
                operation: result.renderFailed ? 'wrap' : 'locate',
                source: marking.source.contentType,
              })
            }
          }
          else if (result.placed) {
            placement.value[marking.id] = 'placed'
          }

          setMarkingNoteIndicator(marking.id, !!marking.note?.trim())
        }
      }
    }

    scrollToTargetMarking()
    discardOwnMutations()
  }

  // Wrapping text and appending note buttons queues mutation records of our own
  // making, which would schedule a second, redundant pass. After a full pass
  // nothing else can be lost with them: it runs to completion synchronously.
  // Reports whether anything was queued, for the callers that can lose records.
  function discardOwnMutations(): boolean {
    let hadRecords = false

    for (const observer of observers.values()) {
      if (observer.takeRecords().length > 0) {
        hadRecords = true
      }
    }

    return hadRecords
  }

  // Bound rather than passed directly: the observer hands its callback the
  // mutation records, which must not arrive as `changedIds`.
  const debouncedRehydrate = useDebounceFn(() => rehydrate(), REHYDRATION_DEBOUNCE_MS)

  function disconnectObservers() {
    for (const observer of observers.values()) {
      observer.disconnect()
    }

    observers.clear()
  }

  function attachObservers() {
    disconnectObservers()

    for (const key of containerKeys) {
      const root = toValue(containers[key])

      if (!root) {
        continue
      }

      const observer = new MutationObserver(debouncedRehydrate)
      observer.observe(root, { childList: true, subtree: true })
      observers.set(key, observer)
    }
  }

  function tryRehydrate(changedIds?: Set<string>) {
    // Rehydration is a DOM operation start to finish: it reads containers,
    // wraps text nodes and attaches MutationObservers. The watchers below run
    // with `immediate: true`, so without this guard a direct page load reaches
    // `document` and `MutationObserver` on the server, where neither exists.
    if (import.meta.server) {
      return
    }

    nextTick(() => {
      rehydrate(changedIds)

      // Interactive elements (commentary switches, footnote toggles, collapsed
      // panels) replace content and destroy applied <mark>s, so the observers
      // outlive the pass. Only a full one rebinds them: a targeted pass follows
      // a store change, where the containers are the same elements.
      if (!changedIds) {
        attachObservers()
      }
    })
  }

  watch([isReady, () => route.fullPath], () => tryRehydrate(), { immediate: true })

  watch(allMarkings, (markings, previous) => {
    const previousById = new Map(previous.map(marking => [marking.id, marking]))
    const changedIds = new Set(previousById.keys())

    for (const marking of markings) {
      const existing = previousById.get(marking.id)

      if (existing && existing.color === marking.color && existing.note === marking.note) {
        changedIds.delete(marking.id)
      }
      else {
        changedIds.add(marking.id)
      }
    }

    if (changedIds.size) {
      tryRehydrate(changedIds)
    }
  })

  // Re-run when a container ref flips from null to an element (template refs
  // settle after mount; panels can be collapsed and re-expanded).
  for (const key of containerKeys) {
    const ref = containers[key]

    watch(ref, (next) => {
      if (next) {
        tryRehydrate()
      }
    })

    // A column can gain or lose layout without any DOM mutation, so the
    // observers above never see it: the commentary column is `hidden lg:flex`,
    // reading views swap columns by class below desktop, and the columns
    // animate width for half a second. Arriving by client-side navigation
    // renders cached content in one go, so the single pass above lands
    // mid-animation and finds nothing to scroll to.
    //
    // Rehydrating again also re-evaluates the note indicators, which only sit
    // in the margin while there is one — resizing must not strand a button on
    // top of the text. `lastScrolledMarkingId` still keeps this to one scroll.
    useResizeObserver(ref, () => {
      scrollToTargetMarking()
      debouncedRehydrate()
    })
  }

  // The watches above can miss the setup-time pass behind a Suspense
  // boundary; onMounted is the deterministic kick after first mount.
  onMounted(() => tryRehydrate())

  onUnmounted(disconnectObservers)
}
